"use client";

import { useEffect, useMemo } from "react";
import { DEFAULT_PRESET, serializeUrlState, type TableUrlState } from "../../app";
import type { FilterClause, SortClause } from "../../domain";
import { logger } from "../smart-table-logger";
import { useTableConfig } from "../table-provider";

export interface UseUrlStateSyncParams {
  page: number;
  pageSize: number;
  filters: FilterClause[];
  sort: SortClause[];
  activePresetId: string | undefined;
  stickyIds: string[];
  hiddenIds: string[];
  enableUrlState: boolean;
}

/** Записывает состояние таблицы в адресную строку через urlAdapter провайдера. */
export function useUrlStateSync({
  page,
  pageSize,
  filters,
  sort,
  activePresetId,
  stickyIds,
  hiddenIds,
  enableUrlState,
}: UseUrlStateSyncParams) {
  const { urlAdapter } = useTableConfig();
  const urlState: TableUrlState = useMemo(
    () => ({
      page,
      pageSize,
      filters,
      sort,
      preset: activePresetId ?? DEFAULT_PRESET,
      stickyColumns: stickyIds,
      hiddenColumns: hiddenIds,
    }),
    [page, pageSize, filters, sort, activePresetId, stickyIds, hiddenIds],
  );

  useEffect(() => {
    if (!enableUrlState) return;
    const newUrl = serializeUrlState(urlState);
    try {
      if (urlAdapter.getSearch() !== newUrl) urlAdapter.replaceSearch(newUrl);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      logger.error(`[SmartTable] url adapter failed: ${message}`);
    }
  }, [urlState, enableUrlState, urlAdapter]);
}
